import angular from "angular";
import { LocalDate, nativeJs } from "@js-joda/core";
import moment, { Moment } from "moment";
import {
  caregiverStatus,
  CaregiverStatus as CaregiverStatusConst,
} from "../../../../scripts/consts/commonConsts";
import { assertNever } from "../../../../scripts/consts/assertNever.const";
import { DateRangeDefaultOptions } from "../../../../scripts/consts/dateRangeDefaultOptions";
import {
  CaregiverRecord,
  CaregiverStatus,
  CaregiverWithDisplayName,
} from "../../../../scripts/messages/caregiver";
import { isCaregiverMemoryImprovementsEnabled } from "@medflyt/webapp-react/src/modules/betaFeatures/betaFeatures.store";
import { CaregiverRecordsService } from "../../../../scripts/services/caregiverRecordsService";
import { CaregiverCertification, CaregiverId } from "../../../../scripts/messages/ids";
import {
  CriminalBackgroundStatus,
  HROnboardingStatistics,
  HROnboardingStatisticsResponse,
} from "../../../../scripts/messages/onboarding_statistics";
import { Api } from "../../../../scripts/services/Api";
import { DatabaseApiService } from "../../../../scripts/services/db";
import { Endpoint } from "../../../../scripts/services/endpoint.service";
import {
  getCaregiverStatusColor,
  getCaregiverStatusLabel,
} from "../../../../scripts/utils/caregiverUtils";
import {
  assertDefined,
  chainBooleanFn,
  heapsPermute,
} from "../../../../scripts/utils/generalUtils";
import { MultiselectCallbackParameter } from "../../../../modules/shared/components/multiselect/multiselect.component";

/**
 * The caregiver this page joins onto a statistics row.
 *
 * Two shapes, because the two data paths are genuinely different objects: the
 * narrowed path gets `CaregiverRecord` from the server, and with the caregiver
 * memory improvements switched off the page still reads the legacy dictionary,
 * whose entries are `CaregiverWithDisplayName`. Only the fields the table shows
 * are used, and those exist in both.
 */
type OnboardingCaregiver = CaregiverRecord | CaregiverWithDisplayName;

type CaregiverTableDataType = HROnboardingStatistics &
  OnboardingCaregiver & {
    searchName: string[];
    /**
     * Digits only, flattened. The two caregiver shapes disagree here - the
     * dictionary carries `{ phoneNumber, type }` objects and the endpoint
     * carries bare strings - so the search reads this instead of either.
     */
    searchPhoneNumbers: string[];
    orientationSort: number;
  };

interface CertificationSelect {
  id: number;
  label: CaregiverCertification;
}

interface StageSelect {
  id: number;
  label: string;
}

interface CriminalBackgroundStatusSelect {
  id: number;
  label: typeof CriminalBackgroundStatus[number];
}

interface Selectable<T = number> {
  id: T;
}

import {
  declareAiCapability,
  provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// Onboarding describing its own filter bar. Its stage list is built from the caregivers on screen
// ("certification - stage"), so it is read when the manifest is built rather than held as a copy.
declareAiCapability({
  id: "onboarding.filter",
  title: "Filter the onboarding caregivers list",
  effect: "prefill",
  description:
    "Sets the filters on Caregivers > Onboarding and re-applies them: a free-text search, certification, the stage the caregiver is at, criminal background status, the caregiver's status, whether the mobile app is installed, and the date range. The page opens on the last month. Only works while that page is open.",
  location: "the filter bar of the Caregivers > Onboarding page",
  scope: "page",
  permissionKey: "view_caregivers_section_list",
  params: [
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text the page matches against the caregiver's details.",
    },
    {
      name: "certification",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getOnboardingOptions("agencyCertifications"),
      description: "Limit to caregivers holding these certifications.",
    },
    {
      name: "stage",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getOnboardingOptions("stages"),
      description: "How far through onboarding the caregiver is, as this page groups them.",
    },
    {
      name: "criminalBackgroundStatus",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getOnboardingOptions("criminalBackgroundStatuses"),
      description: "The state of the caregiver's criminal background check.",
    },
    {
      name: "caregiverStatus",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getOnboardingOptions("certificationSelect"),
      description: "The caregiver's employment status.",
    },
    {
      name: "installed",
      type: "enum",
      required: false,
      multiple: true,
      options: () => getOnboardingOptions("installSelect"),
      description: "Whether the caregiver has the mobile app installed.",
    },
    { name: "from", type: "date", required: false, description: "First day to include, as yyyy-MM-dd." },
    { name: "to", type: "date", required: false, description: "Last day to include, as yyyy-MM-dd." },
  ],
  examples: [
    "onboarding caregivers this week",
    "onboarding with a pending background check",
    "onboarding caregivers with a CNA certification",
  ],
});

let onboardingCtrl: () => Record<string, unknown> | null = () => null;

function getOnboardingOptions(name: string) {
  const list = onboardingCtrl()?.[name];

  return (Array.isArray(list) ? list : []).map((option: { id: unknown; label?: unknown }) => ({
    value: String(option.id),
    label: String(option.label ?? option.id),
  }));
}

//! @ngInject
class Controller implements angular.IComponentController {
  static readonly $name = "onboardingComponent";

  private isLoadingCaregivers = true;
  private isLoadingOnboardingData = true;
  private caregiversMap: Record<number, OnboardingCaregiver> = {};
  private readonly useMemoryImprovements = isCaregiverMemoryImprovementsEnabled();
  private caregiverStatistics!: HROnboardingStatistics[];
  private caregiversData?: CaregiverTableDataType[];
  private caregvierStatisticsTable!: NgTableParams<CaregiverTableDataType>;
  private agencyCertifications!: CertificationSelect[];
  private stages!: StageSelect[];
  private criminalBackgroundStatuses: CriminalBackgroundStatusSelect[];
  private certificationSelect: CaregiverStatusConst["dropdown"];
  private installSelect: { id: boolean; label: string }[];
  private filters: {
    certifications: Selectable<number>[];
    freeText: null | string;
    stages: Selectable<number>[];
    criminalBackgroundStatuses: Selectable<number>[];
    dateRange: [Moment, Moment];
    caregiverStatus: Selectable<string>[];
    installStatus: boolean[];
  };
  private dateRangeOptions: DateRangeDefaultOptions;
  private releaseAiCapability: () => void = () => undefined;

  constructor(
    private $rootScope: angular.IRootScopeService,
    private $scope: ng.IScope,
    private api: Api,
    private endpoint: Endpoint,
    private DatabaseApi: DatabaseApiService,
    private caregiverRecordsService: CaregiverRecordsService,
    private toaster: toaster.IToasterService,
    private NgTableParams: NgTable.ITableParamsConstructor<CaregiverTableDataType>,
    private dateRangeDefaultOptions: DateRangeDefaultOptions
  ) {
    this.filters = {
      certifications: [],
      freeText: null,
      stages: [],
      criminalBackgroundStatuses: [],
      dateRange: [moment().subtract(1, "month"), moment()],
      caregiverStatus: [],
      installStatus: [],
    };

    this.dateRangeOptions = {
      ...dateRangeDefaultOptions,
      opens: "right",
    };

    onboardingCtrl = () => this as unknown as Record<string, unknown>;

    // Note the target: the page's own caregiver-status and install controls write to `filter`
    // (singular) while `applyFilters` reads `filters`, so those two do nothing when a person uses
    // them. This writes where the filtering actually reads, so the AI's filter takes effect.
    this.releaseAiCapability = provideAiCapability("onboarding.filter", (args) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      const pickAll = (listName: string, requested: unknown, what: string) => {
        const available = (this as unknown as Record<string, unknown>)[listName];
        const wanted = asList(requested).map((one) => String(one).toLowerCase());
        const chosen = (Array.isArray(available) ? available : []).filter(
          (option: { id: unknown; label?: unknown }) =>
            wanted.includes(String(option.id).toLowerCase()) ||
            wanted.includes(String(option.label ?? "").toLowerCase())
        );

        if (chosen.length === 0) {
          throw new Error(`This page has no ${what} ${asList(requested).join(" or ")}.`);
        }

        return chosen;
      };
      const parse = (text: unknown) => {
        const value = moment(String(text), "YYYY-MM-DD", true);

        if (!value.isValid()) {
          throw new Error(`Could not read the date ${String(text)}.`);
        }

        return value;
      };

      if (args.search !== undefined) {
        this.filters.freeText = String(args.search);
        applied.push(`"${String(args.search)}"`);
      }

      for (const [arg, listName, key, what] of [
        ["certification", "agencyCertifications", "certifications", "certification"],
        ["stage", "stages", "stages", "stage"],
        ["criminalBackgroundStatus", "criminalBackgroundStatuses", "criminalBackgroundStatuses", "background status"],
        ["caregiverStatus", "certificationSelect", "caregiverStatus", "caregiver status"],
        ["installed", "installSelect", "installStatus", "installation state"],
      ] as const) {
        if (args[arg] === undefined) {
          continue;
        }

        const chosen = pickAll(listName, args[arg], what);

        (this.filters as unknown as Record<string, unknown>)[key] =
          key === "installStatus" ? chosen.map((option) => option.id) : chosen;
        applied.push(chosen.map((option) => String(option.label ?? option.id)).join(" and "));
      }

      if (args.from !== undefined || args.to !== undefined) {
        this.filters.dateRange = [
          args.from === undefined ? moment("1900-01-01") : parse(args.from),
          args.to === undefined ? moment().add(50, "years") : parse(args.to),
        ];
        applied.push([args.from, args.to].filter(Boolean).join(" to "));
      }

      this.applyTableData();

      return applied.length === 0
        ? "Re-applied the onboarding filters"
        : `Showing onboarding: ${applied.join(", ")}`;
    });

    this.$scope.$on("$destroy", () => this.releaseAiCapability());

    this.criminalBackgroundStatuses = CriminalBackgroundStatus.map((label, id) => ({ label, id }));
    this.certificationSelect = caregiverStatus.dropdown;
    this.installSelect = [
      {
        id: false,
        label: "Not Installed",
      },
      {
        id: true,
        label: "Installed",
      },
    ];
  }

  $onInit(): void {
    // Order matters on the narrowed path: the caregivers to fetch are the ones
    // the statistics reference, so that request is issued by fetchOnboardingData
    // once it knows the ids. Off, the dictionary is agency-wide and independent,
    // so it is started here and re-read when it lands.
    this.fetchOnboardingData();
    this.fetchAgencyCertifications();

    if (!this.useMemoryImprovements) {
      this.fetchCaregivers();
      this.$rootScope.$on("got_caregivers_data", () => this.fetchCaregivers());
    }

    this.$rootScope.$on("got_agency_certifications", () => this.fetchAgencyCertifications());
  }

  onInstallStatusSelect = (selected: MultiselectCallbackParameter<boolean>) => {
    this.filters.installStatus = selected.map((x) => x.id);
    this.applyTableData();
  };

  onCaregiverStatusSelect = (selected: MultiselectCallbackParameter<CaregiverStatus>) => {
    this.filters.caregiverStatus = selected;
    this.applyTableData();
  };

  fetchAgencyCertifications = () => {
    this.agencyCertifications = this.DatabaseApi.activeAgencyCertifications().map((val, i) => ({
      id: i,
      label: val.certification,
    }));
  };

  /**
   * The dictionary path, kept for when the caregiver memory improvements are off.
   * Reads whatever the dictionary currently holds, and does nothing until it has
   * landed - `got_caregivers_data` brings it back here.
   */
  fetchCaregivers = () => {
    if (Object.keys(this.caregiversMap).length > 0) {
      return;
    }

    this.caregiversMap = this.DatabaseApi.caregivers();

    if (Object.keys(this.caregiversMap).length === 0) {
      return;
    }

    this.isLoadingCaregivers = false;
    this.initOnboardingCaregiversTable();
  };

  /**
   * The caregivers this page's rows reference, and only those.
   *
   * The rows come from `hr_caregiver_status`, which is bounded by the date range
   * the page is showing - a month by default. Asking for those ids is a join;
   * asking for the agency was the dictionary again under another name.
   */
  private fetchCaregiversForStatistics = (statistics: HROnboardingStatistics[]) => {
    const ids = [...new Set(statistics.map((entry) => entry.caregiverId))];

    if (ids.length === 0) {
      this.caregiversMap = {};
      this.isLoadingCaregivers = false;
      this.initOnboardingCaregiversTable();
      return;
    }

    // Through the service rather than a direct GET: the date range can be widened
    // to years, and the ids travel in the query string.
    return this.caregiverRecordsService
      .byIds(ids, { withPhoneNumbers: true })
      .then((caregivers) => {
        this.caregiversMap = {};
        for (const caregiver of caregivers.values()) {
          this.caregiversMap[caregiver.id as unknown as number] = caregiver;
        }
        this.isLoadingCaregivers = false;
        this.initOnboardingCaregiversTable();
      })
      .catch(() => {
        this.isLoadingCaregivers = false;
        this.toaster.pop("error", "Something went wrong", "Could not fetch caregivers");
      });
  };

  fetchOnboardingData = async () => {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/hr_caregiver_status",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
      },
      queries: {
        from: LocalDate.from(nativeJs(this.filters.dateRange[0])).toString(),
        to: LocalDate.from(nativeJs(this.filters.dateRange[1])).toString(),
      },
    });

    this.api.get<HROnboardingStatisticsResponse>(url).then(({ data: { caregivers } }) => {
      this.caregiverStatistics = caregivers;
      this.isLoadingOnboardingData = false;
      this.initStageSelection();

      if (this.useMemoryImprovements) {
        // Re-fetched on every statistics load, not just the first: changing the
        // date range changes which caregivers the rows reference.
        this.fetchCaregiversForStatistics(caregivers);
        return;
      }

      this.initOnboardingCaregiversTable();
    });
  };

  initOnboardingCaregiversTable = () => {
    if (this.isLoadingCaregivers || this.isLoadingOnboardingData) {
      return;
    }

    this.caregiversData = this.formatCaregiverRows(this.caregiverStatistics);

    this.applyTableData();
  };

  applyTableData = () => {
    if (this.caregiversData === undefined || this.caregiversData.length === 0) {
      return;
    }
    const options = {
      count: 25,
      sorting: { timeStamp: "desc" },
    };

    this.caregvierStatisticsTable = new this.NgTableParams(options, {
      counts: [10, 25, 50, 100],
      dataset: this.applyFilters(this.caregiversData),
    });
  };

  selectableEvents = {
    onItemSelect: this.applyTableData,
    onItemDeselect: this.applyTableData,
    onDeselectAll: this.applyTableData,
  };

  initStageSelection = () => {
    const stageHash = new Set<string>();
    for (const caregiver of this.caregiverStatistics) {
      stageHash.add(this.hashCaregiverStage(caregiver));
    }
    this.stages = [...stageHash.values()].map((label, i) => ({
      id: i,
      label: label,
    }));
  };

  hashCaregiverStage = (caregiver: HROnboardingStatistics) =>
    `${caregiver.certification} - ${caregiver.currentStage.name}`;

  applyFilters = (caregivers: CaregiverTableDataType[]): CaregiverTableDataType[] => {
    const filters: ((val: CaregiverTableDataType) => boolean)[] = [];

    const freeTextFilterFn = this.getFreeTextSearchFilter();

    if (freeTextFilterFn !== undefined) {
      filters.push(freeTextFilterFn);
    }

    const currentStageFilter = this.filters.stages;
    if (currentStageFilter !== null && currentStageFilter.length > 0) {
      filters.push((caregiver) =>
        currentStageFilter.some(
          ({ id }) => this.stages.at(id)?.label === this.hashCaregiverStage(caregiver)
        )
      );
    }

    const certificationFilter = this.filters.certifications;
    if (certificationFilter.length > 0) {
      filters.push((caregiver) =>
        certificationFilter.some(
          ({ id }) => caregiver.certification === this.agencyCertifications.at(id)?.label
        )
      );
    }

    const currentCriminalBackgroundFilter = this.filters.criminalBackgroundStatuses;
    if (currentCriminalBackgroundFilter !== null && currentCriminalBackgroundFilter.length > 0) {
      filters.push((caregiver) =>
        currentCriminalBackgroundFilter.some(
          ({ id }) => this.criminalBackgroundStatuses.at(id)?.label === caregiver.criminalBackground
        )
      );
    }

    const caregiverStatusFilter = this.filters.caregiverStatus;
    if (caregiverStatusFilter.length > 0) {
      filters.push((caregiver) => caregiverStatusFilter.some((x) => x.id === caregiver.status));
    }

    const installFilter = this.filters.installStatus;
    if (installFilter.length > 0) {
      filters.push((caregiver) => installFilter.includes(caregiver.appInstalled));
    }

    const filterFn = filters.reduce(
      (prev, acc) => chainBooleanFn(prev, acc),
      () => true
    );

    return caregivers.filter(filterFn);
  };

  onDateRangeChanged = (startDate: Moment, endDate: Moment) => {
    this.isLoadingOnboardingData = true;
    this.filters.dateRange = [startDate, endDate];
    this.fetchOnboardingData();
  };

  formatCaregiverRows = (
    caregiverStatistics: HROnboardingStatistics[]
  ): CaregiverTableDataType[] => {
    const data: CaregiverTableDataType[] = [];

    for (const caregiver of caregiverStatistics) {
      const caregiverDetails = this.caregiversMap[caregiver.caregiverId as unknown as number];

      // A statistics row whose caregiver is not here has no name, id or photo to
      // render, so it is dropped rather than thrown on. Reachable either way: the
      // dictionary excludes join requests, and the id fetch excludes a caregiver
      // whose agency association has since gone.
      if (caregiverDetails === undefined) {
        continue;
      }

      const namesPremutations = heapsPermute([
        caregiverDetails.firstName.toLowerCase(),
        caregiverDetails.middleName?.toLowerCase(),
        caregiverDetails.lastName.toLowerCase(),
      ]).map((per) =>
        per.filter((x): x is NonNullable<typeof x> => x !== null && x !== undefined).join(" ")
      );
      data.push({
        ...caregiver,
        ...caregiverDetails,
        searchName: namesPremutations,
        searchPhoneNumbers: this.getSearchPhoneNumbers(caregiverDetails),
        orientationSort: this.getOrientationSort(caregiver),
      });
    }

    return data;
  };

  /**
   * The caregiver's phone numbers as bare digit strings, whichever shape they
   * arrived in - `CaregiverRecord` carries strings, the dictionary carries
   * `{ phoneNumber, type }` objects.
   */
  private getSearchPhoneNumbers = (caregiver: OnboardingCaregiver): string[] => {
    const numbers: unknown = (caregiver as { phoneNumbers?: unknown }).phoneNumbers;

    if (!Array.isArray(numbers)) {
      return [];
    }

    return numbers
      .map((entry) =>
        typeof entry === "string" ? entry : String((entry as { phoneNumber?: unknown })?.phoneNumber ?? "")
      )
      .map((number) => number.replace(/\D/g, ""))
      .filter((number) => number.length > 0);
  };

  private getOrientationSort = (caregiver: HROnboardingStatistics): number => {
    switch (caregiver.orientation.type) {
      case "Started":
        return caregiver.orientation.completion;

      case "Not Started":
        return -1;

      default:
        assertNever(caregiver.orientation);
    }
  };

  joinArray = <T>(arr: T[], separator: string): string => {
    return arr.join(separator);
  };

  openAgencyChat = (id: CaregiverId) => {
    this.$rootScope.openAgencyChat(id);
  };

  goToProfile = (id: CaregiverId, certification: string) => {
    this.$rootScope.openCaregiverModal(
      id,
      assertDefined(this.caregiversMap[id as unknown as number], "caregiver"),
      false,
      certification
    );
  };

  getCaregiverStatusColor = getCaregiverStatusColor;

  getCaregiverStatusLabel = getCaregiverStatusLabel;

  private getFreeTextSearchFilter = (): ((val: CaregiverTableDataType) => boolean) | undefined => {
    if (this.filters.freeText === null) {
      return;
    }

    const freeTextSearch = this.filters.freeText.trim().toLowerCase();
    const freeTextSearchNumber = Number(freeTextSearch);
    const freeTextSearchPhoneNumber = freeTextSearch.replace(/\D/g, "");

    return (caregiver) =>
      caregiver.searchName.some((name) => name.includes(freeTextSearch)) ||
      caregiver.displayId === freeTextSearchNumber ||
      (freeTextSearchPhoneNumber.length > 0 &&
        caregiver.searchPhoneNumbers.some((number) =>
          number.includes(freeTextSearchPhoneNumber)
        ));
  };
}

export const onboardingComponent: angular.IComponentOptions = {
  templateUrl: "admin/modules/onboarding/components/onboarding/onboarding.component.html",
  controller: Controller,
  controllerAs: "ctrl",
  bindings: {},
};
